import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Row,
  Col,
  Typography,
  Alert,
  Tooltip,
  Badge,
  message,
  Popconfirm,
  Divider,
  Descriptions,
  Timeline,
  Collapse,
} from 'antd';
import {
  SearchOutlined,
  SettingOutlined,
  MonitorOutlined,
  UserOutlined,
  StopOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  EyeOutlined,
  FlagOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import { AppDispatch, RootState } from '../../../store/store';
import {
  getSTRAccountMonitoringStatusThunk,
  searchSTRAccountMonitoringByAgreementNoThunk,
  disableSTRAccountMonitoringThunk,
  enableSTRAccountMonitoringThunk,
  disableSTRAccountRulesThunk,
  enableSTRAccountRulesThunk,
  getSTRAccountFlagsThunk,
} from '../../../services/strService/strThunk';
import { clearAccountMonitoring, clearAccountFlags } from '../../../features/str/strSlice';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const STRAccountMonitoring: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { accountMonitoring, accountFlags, rules } = useSelector((state: RootState) => state.str);

  const [searchAgreementId, setSearchAgreementId] = useState('');
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionType, setActionType] = useState<'enable' | 'disable' | 'rules'>('enable');
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [currentAccount, setCurrentAccount] = useState('');
  const [form] = Form.useForm();

  // Clear state when component mounts (when tab is switched to)
  useEffect(() => {
    // Reset all local state
    setSearchAgreementId('');
    setShowMonitoringModal(false);
    setShowRulesModal(false);
    setShowDetailsModal(false);
    setActionType('enable');
    setSelectedRules([]);
    setReason('');
    setCurrentAccount('');
    form.resetFields();

    // Clear Redux state
    dispatch(clearAccountMonitoring());
    dispatch(clearAccountFlags());
  }, []); // Empty dependency array means this runs only on mount

  const handleSearch = async () => {
    if (!searchAgreementId.trim()) {
      message.error('Please enter an Agreement ID or Agreement Number');
      return;
    }

    try {
      // Detect if input is an agreement number (format: X-XXXXXX-X) or UUID
      const isAgreementNumber =
        /^\d-\d{6}-\d$/.test(searchAgreementId.trim()) ||
        /^\d{8}$/.test(searchAgreementId.trim().replace(/\D/g, ''));

      if (isAgreementNumber) {
        // Format the agreement number if needed (remove spaces/hyphens and re-format)
        const cleanNumber = searchAgreementId.trim().replace(/\D/g, '');
        const formattedNumber =
          cleanNumber.length === 8
            ? `${cleanNumber.substr(0, 1)}-${cleanNumber.substr(1, 6)}-${cleanNumber.substr(7, 1)}`
            : searchAgreementId.trim();

        await dispatch(searchSTRAccountMonitoringByAgreementNoThunk(formattedNumber));
      } else {
        // Assume it's an agreement ID (UUID)
        await dispatch(getSTRAccountMonitoringStatusThunk(searchAgreementId));
      }
    } catch (error) {
      message.error('Failed to fetch account monitoring status');
    }
  };

  const handleViewDetails = async (agreementId: string) => {
    try {
      await dispatch(getSTRAccountFlagsThunk(agreementId));
      setShowDetailsModal(true);
    } catch (error) {
      message.error('Failed to fetch account details');
    }
  };

  const handleMonitoringAction = async (agreementId: string, action: 'enable' | 'disable') => {
    setCurrentAccount(agreementId);
    setActionType(action);
    setShowMonitoringModal(true);
  };

  const handleRulesAction = async (
    agreementId: string,
    action: 'enable' | 'disable',
    currentRules: string[]
  ) => {
    setCurrentAccount(agreementId);
    setActionType('rules');
    setSelectedRules(action === 'disable' ? [] : currentRules);
    setShowRulesModal(true);
  };

  const submitMonitoringAction = async () => {
    if (!reason.trim()) {
      message.error('Please provide a reason');
      return;
    }

    if (!currentAccount.trim()) {
      message.error('No account selected');
      return;
    }

    try {
      if (actionType === 'enable') {
        await dispatch(enableSTRAccountMonitoringThunk({ agreementId: currentAccount, reason }));
      } else if (actionType === 'disable') {
        await dispatch(disableSTRAccountMonitoringThunk({ agreementId: currentAccount, reason }));
      }

      setShowMonitoringModal(false);
      setReason('');
      setCurrentAccount('');

      // Refresh data
      if (searchAgreementId) {
        await dispatch(getSTRAccountMonitoringStatusThunk(searchAgreementId));
      }
    } catch (error) {
      message.error(`Failed to ${actionType} account monitoring`);
    }
  };

  const submitRulesAction = async () => {
    if (!reason.trim()) {
      message.error('Please provide a reason');
      return;
    }

    if (!currentAccount.trim()) {
      message.error('No account selected');
      return;
    }

    try {
      const currentDisabledRules = accountMonitoring.data?.disabledRules || [];
      const allRules = (rules.data || []).map((rule) => rule.ruleCode);

      // Calculate which rules to enable/disable
      const rulesToDisable = allRules.filter((rule) => !selectedRules.includes(rule));
      const rulesToEnable = selectedRules.filter((rule) => currentDisabledRules.includes(rule));

      if (rulesToDisable.length > 0) {
        await dispatch(
          disableSTRAccountRulesThunk({
            agreementId: currentAccount,
            rules: rulesToDisable,
            reason,
          })
        );
      }

      if (rulesToEnable.length > 0) {
        await dispatch(
          enableSTRAccountRulesThunk({
            agreementId: currentAccount,
            rules: rulesToEnable,
            reason,
          })
        );
      }

      setShowRulesModal(false);
      setReason('');
      setCurrentAccount('');
      setSelectedRules([]);

      // Refresh data
      if (searchAgreementId) {
        await dispatch(getSTRAccountMonitoringStatusThunk(searchAgreementId));
      }
    } catch (error) {
      message.error('Failed to update account rules');
    }
  };

  const renderAccountStatus = () => {
    const account = accountMonitoring.data;
    if (!account) return null;

    const enabledRules = (rules.data || []).filter(
      (rule) => !account.disabledRules.includes(rule.ruleCode)
    );
    const disabledRules = (rules.data || []).filter((rule) =>
      account.disabledRules.includes(rule.ruleCode)
    );

    return (
      <Card title="Account Monitoring Status" className="mb-4">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Agreement ID">
                <Text strong>{account.agreementId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Monitoring Status">
                <Badge
                  status={account.isMonitoringEnabled ? 'processing' : 'error'}
                  text={account.isMonitoringEnabled ? 'Enabled' : 'Disabled'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {moment(account.lastUpdated).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              {account.reason && (
                <Descriptions.Item label="Reason">
                  <Text type="secondary">{account.reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <div className="space-y-3">
              <div>
                <Text strong>Enabled Rules ({enabledRules.length})</Text>
                <div className="mt-2 space-x-1">
                  {enabledRules.map((rule) => (
                    <Tag key={rule.ruleCode} color="green">
                      {rule.ruleCode}
                    </Tag>
                  ))}
                </div>
              </div>
              <div>
                <Text strong>Disabled Rules ({disabledRules.length})</Text>
                <div className="mt-2 space-x-1">
                  {disabledRules.map((rule) => (
                    <Tag key={rule.ruleCode} color="red">
                      {rule.ruleCode}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Divider />

        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(account.agreementId)}
          >
            View Details
          </Button>
          {account.isMonitoringEnabled ? (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={() => handleMonitoringAction(account.agreementId, 'disable')}
            >
              Disable Monitoring
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleMonitoringAction(account.agreementId, 'enable')}
            >
              Enable Monitoring
            </Button>
          )}
          <Button
            icon={<SettingOutlined />}
            onClick={() =>
              handleRulesAction(
                account.agreementId,
                'enable',
                enabledRules.map((r) => r.ruleCode)
              )
            }
          >
            Configure Rules
          </Button>
        </Space>
      </Card>
    );
  };

  const renderAccountDetails = () => {
    const account = accountFlags.data;
    if (!account) return null;

    return (
      <Modal
        title="Account Details"
        open={showDetailsModal}
        onCancel={() => {
          setShowDetailsModal(false);
          dispatch(clearAccountFlags());
        }}
        footer={null}
        width={800}
      >
        <div className="space-y-4">
          {/* Customer Information */}
          <Card size="small" title="Customer Information">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Customer Name">{account.customerName}</Descriptions.Item>
              <Descriptions.Item label="Agreement ID">{account.agreementId}</Descriptions.Item>
              <Descriptions.Item label="Email">{account.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{account.phone}</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Monitoring Status */}
          <Card size="small" title="Monitoring Status">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Status">
                <Badge
                  status={account.monitoringStatus.isEnabled ? 'processing' : 'error'}
                  text={account.monitoringStatus.isEnabled ? 'Enabled' : 'Disabled'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {moment(account.monitoringStatus.lastUpdated).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              {account.monitoringStatus.reason && (
                <Descriptions.Item label="Reason" span={2}>
                  <Text type="secondary">{account.monitoringStatus.reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            <div className="mt-3">
              <Text strong>Disabled Rules:</Text>
              <div className="mt-2 space-x-1">
                {account.monitoringStatus.disabledRules.map((rule) => (
                  <Tag key={rule} color="red">
                    {rule}
                  </Tag>
                ))}
              </div>
            </div>
          </Card>

          {/* Alerts Summary */}
          <Card size="small" title="Alerts Summary">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {account.alertsSummary.totalAlerts}
                  </div>
                  <div className="text-sm text-gray-500">Total Alerts</div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div>
                  <Text strong>By Severity:</Text>
                  <div className="mt-2 space-y-1">
                    {Object.entries(account.alertsSummary.alertsBySeverity).map(
                      ([severity, count]) => (
                        <div key={severity} className="flex justify-between">
                          <Tag
                            color={
                              severity === 'HIGH'
                                ? 'red'
                                : severity === 'MEDIUM'
                                ? 'orange'
                                : 'green'
                            }
                          >
                            {severity}
                          </Tag>
                          <Text>{count}</Text>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div>
                  <Text strong>By Rule:</Text>
                  <div className="mt-2 space-y-1">
                    {Object.entries(account.alertsSummary.alertsByRule)
                      .slice(0, 5)
                      .map(([rule, count]) => (
                        <div key={rule} className="flex justify-between">
                          <Tag color="blue">{rule}</Tag>
                          <Text>{count}</Text>
                        </div>
                      ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Recent Alerts */}
          <Card size="small" title="Recent Alerts">
            <Table
              dataSource={account.alertsSummary.recentAlerts}
              columns={[
                {
                  title: 'Rule',
                  dataIndex: 'ruleCode',
                  key: 'ruleCode',
                  render: (rule: string) => <Tag color="blue">{rule}</Tag>,
                },
                {
                  title: 'Severity',
                  dataIndex: 'severity',
                  key: 'severity',
                  render: (severity: string) => (
                    <Tag
                      color={
                        severity === 'HIGH' ? 'red' : severity === 'MEDIUM' ? 'orange' : 'green'
                      }
                    >
                      {severity}
                    </Tag>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => (
                    <Tag
                      color={
                        status === 'ACTIVE' ? 'red' : status === 'RESOLVED' ? 'green' : 'orange'
                      }
                    >
                      {status}
                    </Tag>
                  ),
                },
                {
                  title: 'Created',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (date: string) => moment(date).format('MM/DD HH:mm'),
                },
              ]}
              pagination={false}
              size="small"
              scroll={{ x: 400 }}
            />
          </Card>
        </div>
      </Modal>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <Title level={4}>Account Monitoring Control</Title>
        <div className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Input
                placeholder="Enter Agreement ID or Agreement Number (e.g., 1-123456-7)"
                value={searchAgreementId}
                onChange={(e) => setSearchAgreementId(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<SearchOutlined />}
                size="large"
              />
            </Col>
            <Col xs={24} md={12}>
              <Button
                type="primary"
                onClick={handleSearch}
                loading={accountMonitoring.isLoading}
                size="large"
              >
                Search Account
              </Button>
            </Col>
          </Row>
        </div>

        {accountMonitoring.isError && (
          <Alert
            message="Error"
            description={accountMonitoring.errorMessage}
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        {accountMonitoring.data && renderAccountStatus()}
        {accountFlags.data && renderAccountDetails()}
      </Card>

      {/* Monitoring Action Modal */}
      <Modal
        title={`${actionType === 'enable' ? 'Enable' : 'Disable'} Monitoring`}
        open={showMonitoringModal}
        onOk={submitMonitoringAction}
        onCancel={() => {
          setShowMonitoringModal(false);
          setReason('');
          setCurrentAccount('');
        }}
        okText={actionType === 'enable' ? 'Enable' : 'Disable'}
        cancelText="Cancel"
      >
        <div className="space-y-4">
          <Alert
            message={`Are you sure you want to ${actionType} monitoring for this account?`}
            type="warning"
            showIcon
          />
          <div>
            <Text strong>Reason:</Text>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Please provide a reason for ${
                actionType === 'enable' ? 'enabling' : 'disabling'
              } monitoring`}
              rows={3}
              required
            />
          </div>
        </div>
      </Modal>

      {/* Rules Configuration Modal */}
      <Modal
        title="Configure Rules"
        open={showRulesModal}
        onOk={submitRulesAction}
        onCancel={() => {
          setShowRulesModal(false);
          setReason('');
          setCurrentAccount('');
          setSelectedRules([]);
        }}
        okText="Update Rules"
        cancelText="Cancel"
        width={600}
      >
        <div className="space-y-4">
          <div>
            <Text strong>Select rules to enable for this account:</Text>
            <Select
              mode="multiple"
              placeholder="Select rules"
              value={selectedRules}
              onChange={setSelectedRules}
              style={{ width: '100%', marginTop: 8 }}
            >
              {(rules.data || []).map((rule) => (
                <Option key={rule.ruleCode} value={rule.ruleCode}>
                  {rule.ruleCode} - {rule.ruleName}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong>Reason:</Text>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide a reason for this rule configuration change"
              rows={3}
              required
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default STRAccountMonitoring;
